import { Box, Button, color } from '@chakra-ui/react';
import { IconButton, ListingButton, Tab } from 'library/atoms';
import { TAG_VARIANTS } from 'library/atoms/Tag/Tag';
import TagStack from 'library/atoms/Tag/TagStack';
import { FilterIcon } from 'library/icons';
import DateTimePickerV2 from 'library/molecules/DateTimePickerV2';
import React, { useMemo, useCallback, useEffect, CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import { ANALYTIC_V3_EVENTS, options } from 'views/main/organization/analytics-v3/constants';
import { useAnalyticsV3Context } from 'views/main/organization/analytics-v3/hooks/useAnalyticsV3Context';
import { useShallow } from 'zustand/react/shallow';
import OnCallAnalytics from '../..';
import { TopCardContainer } from './style';
import { SearchV2 } from 'library/molecules/SearchV2';
import { DropdownOptions } from 'library/molecules';
import moment from 'moment';
import { useOnCallHourContext } from 'views/main/organization/analytics-v3/hooks/useOnCallHourContext';
import { OnCallUsersPerHour } from 'views/main/organization/analytics-v3/api/types';
import { EmailIcon } from '../../../../icons/email';
import { useGetOnCallHourPerUserExport } from 'views/main/organization/analytics-v3/hooks/useGetAnalyticsData';
import { ref } from 'yup';
import { AppTracker } from 'shared/analytics/tracker';
import { prepareDateRangeForAPI } from 'views/main/organization/analytics-v3/helpers';
import { CircleIcon } from 'icons';

function TopCard() {
  const {
    isFilterDrawerOpen,
    setIsFilterDrawerOpen,
    tabIndex,
    setTabIndex,
    filters,
    setFilters,
    dateRange,
    setDateRange,
  } = useAnalyticsV3Context(
    useShallow(state => ({
      isFilterDrawerOpen: state.isFilterDrawerOpen,
      setIsFilterDrawerOpen: state.setIsFilterDrawerOpen,
      tabIndex: state.tabIndex,
      setTabIndex: state.setTabIndex,
      filters: state.onCallUsersFilter,
      setFilters: state.setOnCallUsersFilter,
      dateRange: state.dateRange,
      setDateRange: state.setDateRage, // Fixed typo
    })),
  );
  const { refetch, isLoading: Exporting } = useGetOnCallHourPerUserExport();
  const resetOnCallUserPerHourPagination = useOnCallHourContext(
    state => state.resetOnCallUserPerHourPagination,
  );

  const schedule = filters?.schedule || [];
  const scheduleOwner = filters?.scheduleOwner || [];

  const selectedScheduleOwner = useMemo(
    () =>
      !scheduleOwner?.meAndMySquads
        ? [
            ...(scheduleOwner?.users ?? []),
            ...(scheduleOwner?.squads ?? []),
            ...(scheduleOwner?.myUsers ?? []),
            ...(scheduleOwner?.mySquads ?? []),
          ]
        : [
            {
              label: 'You and Your Squads',
              value: 'meAndMySquads',
              username: '',
              meta: {
                filterType: 'assignee',
              },
            },
            ...(scheduleOwner?.users ?? []),
            ...(scheduleOwner?.squads ?? []),
          ],
    [scheduleOwner],
  );

  const appliedFilters: DropdownOptions[] = useMemo(
    () => [
      ...schedule.map((schedule: any) => ({
        label: `Schedule : ${schedule.label}`,
        value: schedule.value,
        meta: { filterType: 'schedule' },
      })),
      ...selectedScheduleOwner.map(scheduleOwner => ({
        label: `Schedule Owner : ${scheduleOwner.label} (${scheduleOwner.username})`,
        meta: { filterType: 'scheduleOwner' },
        value: scheduleOwner.value,
      })),
    ],
    [schedule, scheduleOwner],
  );

  const onFilter = useCallback(() => {
    setIsFilterDrawerOpen(!isFilterDrawerOpen);
  }, [isFilterDrawerOpen]);

  const handleDateFilterApply = useCallback((startDate: Date, endDate: Date, timeZone: string) => {
    const { startDate: startDateUTC, endDate: endDateUTC } = prepareDateRangeForAPI({
      startDate,
      endDate,
      timeZone,
    });
    AppTracker.track(
      ANALYTIC_V3_EVENTS.INCIDENT_ANALYTICS_DATE_FILTER_APPLIED,
      {
        'Analytics Scope': 'Team Analytics',
        'Analytics Type': 'On-Call Analytics',
        'Analytics Tab': 'On-Call Analytics',
        'Start Date': startDateUTC,
        'End Date': endDateUTC,
        Timezone: timeZone,
      },
      true,
    );
    setDateRange({ startDate, endDate, timeZone });
  }, []);

  const onClose = useCallback(
    (index: number) => {
      const filter = appliedFilters[index];
      if (filter.meta?.filterType === 'schedule') {
        setFilters({
          ...filters,
          schedule: filters.schedule.filter(p => p.value !== filter.value),
        });
      }

      if (filter.meta?.filterType === 'scheduleOwner') {
        if (filter.label.includes('You and Your Squads')) {
          setFilters({
            ...filters,
            scheduleOwner: {
              ...filters.scheduleOwner,
              meAndMySquads: false,
              myUsers: [],
              mySquads: [],
            },
          });
        } else {
          setFilters({
            ...filters,
            scheduleOwner: {
              ...filters.scheduleOwner,
              users: filters.scheduleOwner.users.filter(u => u.value !== filter.value),
              squads: filters.scheduleOwner.squads.filter(s => s.value !== filter.value),
            },
          });
        }
      }
    },
    [appliedFilters],
  );

  const handleSearch = useCallback(
    (searchTerm: string) => {
      resetOnCallUserPerHourPagination();
      setFilters({
        ...filters,
        searchTerm: searchTerm,
      });
    },
    [filters],
  );
  const { pathname } = useLocation();
  const currentOption = options.find(option => option.value === pathname);
  const handleExport = useCallback(() => {
    refetch();
  }, []);
  return (
    <TopCardContainer>
      <div className="top-card-header">
        <div className="top-card-header-title" data-testid="top-card-header-title">
          {currentOption?.label}
        </div>
        <div className="cta-container">
          <Box style={{ maxWidth: '250px' }}>
            <SearchV2
              iconStyle={{ height: '36px', width: '36px' }}
              onSearch={(term: string) => {
                handleSearch(term);
              }}
              isDisabled={false}
              searchValue={filters.searchTerm}
            />
          </Box>
          <IconButton
            aria-label="Filter"
            variant="header"
            style={{ height: '36px', width: '36px' }}
            icon={<FilterIcon style={{ width: 16, height: 16, margin: 'auto' }} />}
            onClick={onFilter}
          />
          {Boolean(appliedFilters.length) && (
            <CircleIcon
              style={{
                fill: 'red',
                height: '10px',
                position: 'absolute',
                stroke: 'red',
                marginLeft: '78px',
                marginTop: '-4px',
                // top: '-5px',
                width: '10px',
              }}
            />
          )}
          <div className="divider" />
          <DateTimePickerV2
            timezone={dateRange.timeZone}
            selectedDates={[dateRange.startDate, dateRange.endDate]}
            minDate={moment().endOf('day').subtract(6, 'months').toDate()}
            maxDate={moment().endOf('day').toDate()}
            onUpdateRange={() => {}}
            dateString=""
            onApply={handleDateFilterApply}
            calculateMaxDate={true}
          />
          <ListingButton
            leftIcon={<EmailIcon />}
            title="Email Report"
            isLoading={Exporting}
            onClick={handleExport}
          />
        </div>
      </div>
      <div className="top-card-body-container">
        <p className="top-card-body">
          View and download reports of historical data on your team's performance. Learn more about
          team analytics{' '}
          <a
            target="_blank"
            rel="noreferrer"
            style={{ color: '#0078FF' }}
            href={'https://support.squadcast.com/analytics/on-call-hours-per-user'}
          >
            here
          </a>
        </p>
        <Box mt={'3'}>
          <TagStack
            tags={appliedFilters}
            variant={TAG_VARIANTS.PLAIN}
            onClose={onClose}
            defaultLength={6}
          />
        </Box>
      </div>
    </TopCardContainer>
  );
}

export default React.memo(TopCard);
